import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, Typography } from '@mui/material';
import moment from 'moment';

import Survey from '../components/survey/Survey';
import Review, { SubmitReviewData } from '../components/review/Review';
import ContentBox from '../components/layout/ContentBox';
import {
  showNotification,
  setExtendedReview,
  setLoading,
} from '../redux/actions';
import OpenQuestionsDialog from '../components/review/OpenQuestionsDialog';
import ApiClient from '../loopback/apiClient';
import { convertToQid } from '../utilities/stringHelper';
import {
  RootState,
  ExtendedReview,
  Review as ReviewType,
  AssignedReviewSubmission,
  Submission,
  Survey as SurveyType,
  Trial,
  UserKeys,
  InstituteKeys,
  ReviewSurvey,
  SurveyPayload,
  SurveySection,
} from '../redux/types';
import withRouter, { RouteType } from '../hoc/withRouter';

type State = {
  currentSection: number;
  showOpenQuestions: boolean;
  review?: ReviewType;
}

class ReviewSubmissionView extends Component<Props, State> {
  static removeOpenQuestions = (payload: SurveyPayload): SurveyPayload => ({
    ...payload,
    sections: payload.sections.map((section: SurveySection): SurveySection => {
      const trimmedSection = { ...section, contextQuestions: [] } as SurveySection;
      return trimmedSection;
    }),
  });

  constructor(props: Props) {
    super(props);
    this.state = {
      currentSection: 0,
      showOpenQuestions: false,
    };
  }

  componentDidMount() {
    const {
      setExtendedReview: setExtendedReviewAction,
      extendedReview,
      router,
    } = this.props;
    const { trial, submission } = router.location.state as locationStateType;
    // eslint-disable-next-line no-undef
    window.scrollTo(0, 0);
    if (trial.reviewCategoryId && trial.reviewCategoryId === 3) {
      if (trial.id && (!extendedReview || extendedReview.trialId !== trial.id)) {
        ApiClient.getExtendedReview(trial.id)
          .then((val) => {
            if (val.length) {
              setExtendedReviewAction(val[0]);
            }
          }).catch((e) => null);
      }
    } else {
      setExtendedReviewAction(undefined);
    }
    if (submission) {
      ApiClient.getReviewFromSubmission(submission.id)
        .then((review) => this.setState({ review }));
    }
  }

  cancelReview = (reviewRequestId: number, enrollmentId: number) => {
    const { router } = this.props;
    router.navigate(`/reviews?reviewedAssignment=${reviewRequestId}&reviewedEnrollment=${enrollmentId}`);
  };

  submitReview = async (
    review: ReviewType,
    reviewRequestId: number,
    enrollmentId: number,
  ) => {
    const { router, showNotification: showNotificationAction } = this.props;
    try {
      await ApiClient.submitReview({ ...review, reviewRequestId });
      router.navigate(`/reviews?reviewedAssignment=${reviewRequestId}&reviewedEnrollment=${enrollmentId}`);
    } catch (e) {
      showNotificationAction(`Update failed - Certificate already approved (${review.id})?`);
    }
  };

  submitReviewHandler = async (reviewData: SubmitReviewData) => {
    const { userId, router, setLoading: setLoadingAction } = this.props;
    setLoadingAction(true);
    if (userId === undefined) {
      return;
    }
    const { review: reviewFromSubmission } = this.state;
    const state = router.location && router.location.state as locationStateType;
    const {
      submission,
      enrollment,
      reviewRequestId,
    } = state;
    const {
      justification, sectionJustifications, multiCommentReview, reviewChanges, ...payload
    } = reviewData;
    const review = {
      submissionId: submission.id,
      reviewerId: userId,
      payload,
      justification,
      sectionJustifications,
      lastUpdatedAt: moment.utc().toISOString(),
      extendedPayload: multiCommentReview
      && multiCommentReview.length
      && { sections: multiCommentReview },
    } as ReviewType;
    const existingReview = reviewFromSubmission;

    if (existingReview
      && Object.keys(existingReview).length
      && reviewChanges
      && Object.keys(reviewChanges).length) {
      ApiClient.saveReviewHistory({
        createdAt: moment.utc().toISOString(),
        payload: reviewChanges,
        reviewerId: userId,
        reviewId: existingReview.id,
      });
    }

    await this.submitReview(
      existingReview ? { ...review, id: existingReview.id } : review,
      reviewRequestId,
      enrollment.id,
    );
    setLoadingAction(false);
  };

  render() {
    const { router } = this.props;
    const state = router.location && router.location.state as locationStateType;
    if (!state) {
      return <ContentBox title="No Data Available" />;
    }
    const { extendedReview } = this.props;
    const {
      survey,
      submission,
      quipUser,
      enrollment,
      reviewRequestId,
      reviewSurvey,
      institute,
    } = state;
    const {
      currentSection,
      showOpenQuestions,
      review: ReviewFromSubmission,
    } = this.state;
    const headLine = {
      user: enrollment.sampleset || `${convertToQid(quipUser.id, institute?.country || quipUser.userCountry)}`,
    };

    return (
      <>
        <ContentBox
          elevation={0}
          title={(
            <FormattedMessage
              id="review.submissionHeadline"
              values={headLine}
            />
          )}
        >
          {
            enrollment.groundTruthId && (
              <div style={{ textAlign: 'right' }} className="mb3">
                <Typography variant="subtitle1">
                  groundTruthId:&nbsp;
                  {enrollment.groundTruthId}
                </Typography>
              </div>
            )
          }
          <Survey
            surveyPayload={ReviewSubmissionView.removeOpenQuestions(survey.payload)}
            submissionPayload={submission.payload}
            currentSection={currentSection}
            onCurrentSectionChange={
              (sectionIdx: number) => this.setState({ currentSection: sectionIdx })
            }
            optionalSections={!!survey.optionalSections}
          />
          <div className="ml2">
            <div
              style={{ textAlign: 'right' }}
            >
              <Button
                variant="contained"
                color="primary"
                className="mr5"
                onClick={() => this.setState({ showOpenQuestions: true })}
              >
                <FormattedMessage id="review.showOpenQuestions" />
              </Button>
              <OpenQuestionsDialog
                isOpen={showOpenQuestions}
                onClose={() => this.setState({ showOpenQuestions: false })}
                submission={submission}
                currentSectionIdx={currentSection}
                survey={survey}
              />
            </div>
            <div>
              {
                enrollment.evaluation.success && (
                  <Typography variant="h6" color="green">
                    <FormattedMessage
                      id="reviewmanagement.passStatus"
                      values={{ points: enrollment.evaluation.overallScore }}
                    />
                  </Typography>
                )
              }
              {
                !enrollment.evaluation.success && (
                  <Typography variant="h6" color="secondary">
                    <FormattedMessage
                      id="reviewmanagement.failStatus"
                      values={{ points: enrollment.evaluation.overallScore }}
                    />
                  </Typography>
                )
              }
            </div>
          </div>
        </ContentBox>
        <hr />
        <ContentBox elevation={0} title={<FormattedMessage id="review.reviewHeadline" />}>
          <Review
            survey={survey.payload}
            onSubmitReview={this.submitReviewHandler}
            onCancelReview={
              () => this.cancelReview(reviewRequestId, enrollment.id)
            }
            reviewSubmission={ReviewFromSubmission}
            currentSection={currentSection}
            onCurrentSectionChange={
              (sectionIdx: number) => this.setState({ currentSection: sectionIdx })
            }
            evaluation={enrollment.evaluation}
            submission={submission}
            extendedReview={extendedReview}
            reviewSurvey={reviewSurvey}
          />
        </ContentBox>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  userId: state.users.token?.userId,
  extendedReview: state.review.extendedReview,
});

const mapDispatchToProps = (dispatch: any) => ({
  setExtendedReview: (
    extendedReview?: ExtendedReview,
  ) => dispatch(setExtendedReview(extendedReview)),
  showNotification: (message: string) => dispatch(showNotification(message)),
  setLoading: (loading: boolean) => dispatch(setLoading(loading)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type locationStateType = {
  submission: Submission,
  enrollment: AssignedReviewSubmission['enrollment'],
  survey: SurveyType,
  trial: Trial,
  reviewRequestId: number,
  quipUser: UserKeys,
  institute: InstituteKeys
  reviewSurvey: ReviewSurvey,
}
type LocationProps = RouteType;

type Props = LocationProps & PropsFromRedux;
export default connector(withRouter(ReviewSubmissionView));
